import { NavLink, useLocation } from 'react-router-dom';
import { PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import type { Permission } from '@insider/shared';
import { cn } from '@/lib/cn';
import { useAuth } from '@/auth/AuthProvider';
import { NAVIGATION } from './navigation';

/**
 * Narrow, understated sidebar (~216px expanded, ~52px collapsed).
 *
 * It is a wayfinding aid, not the main subject of the screen, so it carries no
 * counts, badges or colour. Collapsed it keeps icons with tooltips, which
 * remains usable rather than becoming decorative.
 */
export function Sidebar({
  collapsed,
  onToggle,
}: {
  collapsed: boolean;
  onToggle: () => void;
}) {
  const { can, user } = useAuth();
  const location = useLocation();

  const allowed = (permission?: Permission) => !permission || can(permission);

  return (
    <aside
      className={cn(
        'flex h-full shrink-0 flex-col border-r border-[var(--color-border)] bg-[var(--color-surface)] transition-[width] duration-150',
        collapsed ? 'w-[52px]' : 'w-[216px]',
      )}
    >
      <nav className="flex-1 overflow-y-auto px-2 py-3">
        {NAVIGATION.map((group, groupIndex) => {
          const visible = group.items.filter((item) => allowed(item.permission));
          if (visible.length === 0) return null;

          return (
            <div key={group.label ?? `group-${groupIndex}`} className={groupIndex > 0 ? 'mt-4' : undefined}>
              {group.label && !collapsed ? (
                <p className="mb-1 px-2 text-[10.5px] font-semibold uppercase tracking-wider text-[var(--color-text-subtle)]">
                  {group.label}
                </p>
              ) : null}
              {group.label && collapsed ? (
                <div className="mx-2 mb-1.5 border-t border-[var(--color-border)]" />
              ) : null}

              <ul className="space-y-0.5">
                {visible.map((item) => {
                  // Prefix matching so detail routes keep their section active.
                  const active = item.matchPrefix
                    ? location.pathname === item.matchPrefix ||
                      location.pathname.startsWith(`${item.matchPrefix}/`)
                    : location.pathname === item.to;
                  const Icon = item.icon;

                  return (
                    <li key={item.to}>
                      <NavLink
                        to={item.to}
                        title={collapsed ? item.label : undefined}
                        className={cn(
                          'flex items-center gap-2 rounded-[var(--radius-sm)] px-2 py-1.5 text-[13px] transition-colors',
                          collapsed && 'justify-center',
                          active
                            ? 'bg-[var(--color-hover)] font-medium text-[var(--color-text)]'
                            : 'text-[var(--color-text-muted)] hover:bg-[var(--color-hover)] hover:text-[var(--color-text)]',
                        )}
                      >
                        <Icon className="h-[15px] w-[15px] shrink-0" />
                        {!collapsed ? <span className="truncate">{item.label}</span> : null}
                      </NavLink>
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </nav>

      <div className="border-t border-[var(--color-border)] p-2">
        <button
          type="button"
          onClick={onToggle}
          className={cn(
            'flex w-full items-center gap-2 rounded-[var(--radius-sm)] px-2 py-1.5 text-[12px]',
            'text-[var(--color-text-subtle)] transition-colors hover:bg-[var(--color-hover)] hover:text-[var(--color-text)]',
            collapsed && 'justify-center',
          )}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? (
            <PanelLeftOpen className="h-[15px] w-[15px]" />
          ) : (
            <>
              <PanelLeftClose className="h-[15px] w-[15px]" />
              <span>Collapse</span>
            </>
          )}
        </button>
        {!collapsed && user ? (
          <p className="mt-1 truncate px-2 text-[11px] text-[var(--color-text-subtle)]">
            {user.username} · {user.role.replace('_', ' ').toLowerCase()}
          </p>
        ) : null}
      </div>
    </aside>
  );
}
